import * as _ from 'lodash'
import { action, computed, observable, reaction, toJS } from 'mobx'
import { backend } from '../api'
import {
  AppointmentType,
  ContactDetailInput,
  ExternalFee,
  FuneralType,
  Graveyard,
  Interest,
  MandateForm,
  OfferInput,
  Product,
  ProductType,
  TimeOfDay
} from '../resources'
import CatalogueStore from './CatalogueStore'

export default class ConfiguratorStore {
  @observable private offerLoaded: boolean

  @observable private catalogueLoaded: boolean

  @observable private activeEditCard: boolean
  @observable private activeEditObituary: boolean

  @observable private offer: OfferInput

  @observable private welcomePopup: boolean

  @observable private contactDetailsPopup: boolean

  @observable private packageSelectionPopup: boolean

  @observable private mandateDownloadUrl: string

  private catalogue: CatalogueStore

  private saveOfferTimeout: NodeJS.Timeout

  private nullOffer = {
    uuid: null,
    orderId: null,
    creationDate: null,
    location: {
      province: null,
      city: null,
      zipCode: null,
      latitude: null,
      longitude: null,
      available: null,
      label: null,
      displayCategories: null
    },
    funeralType: null,
    planningReason: null,
    deceasedLocation: {
      locationType: null,
      street: null,
      number: null,
      zipCode: null,
      city: null,
      country: null
    },
    contactDetails: {
      greeting: null,
      firstName: null,
      lastName: null,
      email: null,
      phone: null
    },
    customerAgent: {
      name: null,
      photo: null
    },
    comments: null,
    products: [],
    externalFees: [],
    discounts: [],
    totalAmountFees: null,
    totalAmount: null,
    grandTotal: null,
    interests: [],
    graveyard: {
      uuid: null,
      name: null,
      address: null,
      region: null,
      zipCode: null,
      country: null,
      latitude: null,
      longitude: null,
      alreadyOwnGrave: false
    },
    provision: {
      provisionReason: null,
      timeFrame: null,
      progress: null,
      birthYear: null,
      includeTrustAccountCosts: true
    },
    beneficiary: null,
    offerDownloadUrl: null,
    documents: [],
    orderConfirmed: false,
    interestedInFinancing: false,
    appointment: null,
    testOffer: false,
    syncWithZoho: false,
    sendNotifications: false,
    activeEditCard: false,
    activeEditObituary: false
  }

  constructor() {
    this.offerLoaded = false
    this.catalogueLoaded = false
    this.offer = this.nullOffer
    this.welcomePopup = false
    this.contactDetailsPopup = false
    this.activeEditCard = false
    this.catalogue = new CatalogueStore()
  }

  initWithUuid(uuid: string) {
    const query = new URLSearchParams(location.search)
    if(query.has('heyflow')){
      // this delay is necessary because heyflow triggers the offer creation asynchronously, and we need
      // to index the offer in elasticsearch before being fetched.
      setTimeout(() =>
          this.findOfferByUuid(uuid).then(() => {
            this.init()
          }), 4000);
    }else {
      this.findOfferByUuid(uuid).then(() => {
        this.init()
      })
    }
  }

  initWithOffer(offer: OfferInput) {
    this.setOffer(offer)
    this.welcomePopup = true
    this.init()
  }

  private init = () => {
    this.catalogue.init(this.offer).then(() => {
      this.catalogueLoaded = true
      let showPackageSelection =
        this.catalogue.packages != null &&
        this.catalogue.packages.length > 0 &&
        this.getOffer.products.length == 1
      this.setPackageSelectionPopup(showPackageSelection)
    })
    this.activeEditCard = true
    this.activeEditObituary = true
  }

  @action setWelcomePopup = (show: boolean) => {
    if (!this.catalogue.isFuneralHomeLoaded && show) {
      reaction(
        () => !this.catalogue.isFuneralHomeLoaded,
        () => {
          this.welcomePopup = true
        }
      )
    } else {
      this.welcomePopup = show
    }
  }

  @computed get showWelcomePopup() {
    return this.welcomePopup
  }

  @computed get isContactDetailsMissing() {
    return !this.getOffer.contactDetails
  }

  @action setContactDetailsPopup = (show: boolean) => {
    this.contactDetailsPopup = show
  }

  @computed get showContactDetailsPopup() {
    return this.contactDetailsPopup
  }

  @action setPackageSelectionPopup = (show: boolean) => {
    this.packageSelectionPopup = show
  }

  @computed get showPackageSelectionPopup() {
    return this.packageSelectionPopup
  }

  get getOffer() {
    return toJS(this.offer)
  }

  get getCatalogue() {
    return this.catalogue
  }

  @computed get isAllLoaded() {
    return this.offerLoaded && this.catalogueLoaded
  }

  @computed get isTestingOffer() {
    return this.offer.testOffer
  }

  @computed get getSyncWithZoho() {
    return this.offer.syncWithZoho
  }

  @computed get getSendNotification() {
    return this.offer.sendNotifications
  }

  @computed get getActiveEditCard() {
    return this.activeEditCard
  }
  @computed get getActiveEditObituary() {
    return this.activeEditObituary
  }

  @computed get isOfferLoaded() {
    return this.offerLoaded
  }

  @computed get isCatalogueLoaded() {
    return !!this.catalogueLoaded
  }

  @action setSyncWithZoho = (syncWithZoho: boolean) => {
    this.offer.syncWithZoho = syncWithZoho
    this.saveOffer(false)
  }

  @action setSendNotifications = (sendNotifications: boolean) => {
    this.offer.sendNotifications = sendNotifications
    this.saveOffer(false)
  }

  @action setOffer = (offer: OfferInput) => {
    this.offerLoaded = true
    const saveOffer = offer
    if (!saveOffer.provision) saveOffer.provision = this.nullOffer.provision
    if (!saveOffer.graveyard) saveOffer.graveyard = this.nullOffer.graveyard
    if (!saveOffer.interests) saveOffer.interests = this.nullOffer.interests
    if (!saveOffer.documents) saveOffer.documents = this.nullOffer.documents
    if (!saveOffer.externalFees) {
      saveOffer.externalFees = this.nullOffer.externalFees
      saveOffer.totalAmountFees = this.nullOffer.totalAmountFees
    }
    this.offer = saveOffer
  }

  @action changeCardDesign = (product: Product) => {
    let success = false
    return backend
      .changeCardObituaryDesign(this.offer.uuid, product.uuid, (result) => {
        if (result) {
          product.externalUrl = result.toString()
          this.removeProductByUuidWithoutSave(product.uuid)
          this.offer.products.push(product)
          success = true
        }
      })
      .then(() => {
        this.setActiveEditCard(success)
        return success
      })
  }
  @action changeObituaryDesign = (product: Product) => {
    let success = false
    return backend
      .changeCardObituaryDesign(this.offer.uuid, product.uuid, (result) => {
        if (result) {
          product.externalUrl = result.toString()
          this.removeProductByUuidWithoutSave(product.uuid)
          this.offer.products.push(product)
          success = true
        }
      })
      .then(() => {
        this.setActiveEditObituary(success)
        return success
      })
  }

  @action setOfferGraveyardOwnGrave = (hasAlreadyOwn: boolean) => {
    this.offer.graveyard.alreadyOwnGrave = hasAlreadyOwn
    this.saveOffer(false)
  }

  @action setOfferContactDetails = (
    contactDetails: ContactDetailInput,
    callback?: () => void,
    preventSaveOffer: boolean = false
  ) => {
    this.offer.contactDetails = contactDetails
    if (!preventSaveOffer) this.performSaveOffer(false, callback)
  }

  @action setOfferGraveyardByUuid = (uuid: string) => {
    const graveyard = this.catalogue.graveyards.find((g) => {
      return g.uuid === uuid
    })
    if (graveyard) this.offer.graveyard = graveyard
    this.saveOffer(false)
  }

  @action setOfferGraveyardbyObject = (graveyard: Graveyard) => {
    if (graveyard) this.offer.graveyard = graveyard
    this.saveOffer(false)
  }

  @action setOfferGraveyard = (field: string, value) => {
    this.offer.graveyard[field] = value
    this.saveOffer(false)
  }

  @action setOfferGraveyardClear = () => {
    this.offer.graveyard = this.nullOffer.graveyard
    this.saveOffer(false)
  }

  @action addProduct = (product: Product, quantity: number = 1) => {
    this.offer.products.push(product)
    this.updateQuantity(product, quantity)
    this.saveOffer(false)
  }

  // TODO make it more transactional (setting the whole variable when done editing)
  // better for observable
  @action removeProduct = (uuid: Product) => {
    this.removeProductByUuid(uuid.uuid)
  }

  @action removeProductByUuid = (uuid: string) => {
    toJS(this.offer.products).forEach((product, key) => {
      if (uuid === product.uuid) this.offer.products.splice(key, 1)
    })
    this.saveOffer(false)
  }

  @action removeProductByUuidWithoutSave = (uuid: string) => {
    toJS(this.offer.products).forEach((product, key) => {
      if (uuid === product.uuid) this.offer.products.splice(key, 1)
    })
  }

  @action updateQuantity = (product: Product, quantity: number) => {
    const tempOffer = this.offer
    toJS(tempOffer.products).forEach((productObj, key) => {
      if (product.uuid === productObj.uuid) {
        tempOffer.products[key].quantity = quantity
        tempOffer.products[key].price = this.calculateProductPrice(
          product,
          quantity
        )
      }
    })
    this.offer = tempOffer
    this.saveOffer(false)
  }

  @action toggleProduct = (
    product: Product,
    quantity: number = 1,
    allowDuplicateType?: boolean
  ) => {
    if (this.offerHasProduct(product)) {
      this.removeProduct(product)
    } else {
      if (!allowDuplicateType) {
        this.cleanProductsByProductType(product.productType)
      }
      this.addProduct(product, quantity)
    }
  }

  @action toggleInterest = (interest: Interest) => {
    if (this.offerHasInterest(interest)) {
      toJS(this.offer.interests).forEach((i, k) => {
        if (interest.uuid === i.uuid) this.offer.interests.splice(k, 1)
      })
    } else {
      this.offer.interests.push(interest)
    }
    this.saveOffer(false)
  }

  @action getActiveUrn = () => {
    const urns = this.catalogue.getProductsByType('URN')
    const urnUuids = urns ? urns.map((urn) => urn) : null
    let activeUrn: any = null
    if (urnUuids) {
      urnUuids.forEach((uuid) => {
        toJS(this.offer.products).forEach((res) => {
          if (res.uuid === uuid.uuid) {
            activeUrn = res
          }
        })
      })
    }
    return activeUrn
  }

  @action getActiveCoffin = () => {
    const coffins = this.catalogue.getProductsByType('COFFIN')
    const coffinUuids = coffins ? coffins.map((coffin) => coffin) : null
    let activeCoffin: any = null
    if (coffinUuids) {
      coffinUuids.forEach((uuid) => {
        toJS(this.offer.products).forEach((res) => {
          if (res.uuid === uuid.uuid) {
            activeCoffin = res
          }
        })
      })
    }
    return activeCoffin
  }

  @action getActiveFlower = () => {
    const flowers = this.catalogue.getProductsByType('FLOWER')
    const flowerUuids = flowers ? flowers.map((flower) => flower) : null
    let activeFlower: any = null
    if (flowerUuids) {
      flowerUuids.forEach((uuid) => {
        toJS(this.offer.products).forEach((res) => {
          if (res.uuid === uuid.uuid) {
            activeFlower = res
          }
        })
      })
    }
    return activeFlower
  }

  @action getActiveCard = () => {
    const cards = this.catalogue.getProductsByType('CARD')
    const cardUuids = cards ? cards.map((card) => card) : null
    let activeCard: any = null
    if (cardUuids) {
      cardUuids.forEach((uuid) => {
        toJS(this.offer.products).forEach((res) => {
          if (res.uuid === uuid.uuid) {
            activeCard = res
          }
        })
      })
    }
    return activeCard
  }

  @action getActiveObituary = () => {
    const obis = this.catalogue.getProductsByType('OBITUARY')
    const obiUuids = obis ? obis.map((obi) => obi) : null
    let activeObi: any = null
    if (obiUuids) {
      obiUuids.forEach((uuid) => {
        toJS(this.offer.products).forEach((res) => {
          if (res.uuid === uuid.uuid) {
            activeObi = res
          }
        })
      })
    }
    return activeObi
  }

  @action setDocumentsEntry = (type: string, documents) => {
    const newDocuments = toJS(this.offer.documents).filter(
      (d) => d.type !== type
    )
    documents.forEach((e) => {
      const data = []
      const keys = e.keys()
      let key = keys.next()
      while (!key.done) {
        data.push({ key: key.value, value: e.get(key.value) })
        key = keys.next()
      }
      newDocuments.push({ type, data })
    })
    this.offer.documents = newDocuments
    this.saveOffer(false)
  }

  setComment = (comment, callback: () => void) => {
    const tempOffer = this.offer
    tempOffer.comments = comment
    this.offer = tempOffer
    this.saveOffer(false, callback)
  }

  setActiveEditCard = (status) => {
    this.activeEditCard = status
  }
  setActiveEditObituary = (status) => {
    this.activeEditObituary = status
  }

  @action confirmOrder = () => {
    let success = false
    return backend
      .confirmOffer(this.offer.uuid, (result) => {
        if (result && result.status === 200) {
          this.offer.orderConfirmed = true
          success = true
        }
      })
      .then(() => {
        return success
      })
  }

  @action sendSharingEmail = (email) => {
    let success = false
    return backend
      .sendSharingEmail(this.offer.uuid, email, (result) => {
        if (result && result.status === 200) {
          success = true
        }
      })
      .then(() => {
        return success
      })
  }

  @action scheduleAppointment = (
    type: AppointmentType,
    time: TimeOfDay,
    date: string
  ) => {
    this.offer.appointment = { type, date, time }
    return backend.scheduleAppointment(this.offer.uuid, date, time, type)
  }

  @action setInterestedInFinancing = (interested: boolean) => {
    this.offer.interestedInFinancing = interested
    this.saveOffer(false)
  }

  @action sendWarrant = (formData: MandateForm) => {
    let downloadUrl = ''
    return backend
      .sendMandate(this.offer.uuid, formData, (result) => {
        if (
          result &&
          result.status === 200 &&
          !(result.data.data.submitMandate.indexOf('ERROR') >= 0)
        ) {
          downloadUrl = result.data.data.submitMandate
        }
      })
      .then(() => {
        this.mandateDownloadUrl = downloadUrl
        return downloadUrl
      })
  }

  getDocumentsEntriesByType = (type: string) => {
    const filtered = toJS(this.offer.documents).filter((e) => e.type === type)
    return filtered.map((e) => {
      const obj = new Map(
        e.data.map((v) => {
          return [v.key, v.value]
        })
      )
      return obj
    })
  }

  @action findOfferByUuid = (uuid: any) => {
    return backend.findOfferByUuid(uuid, (result) => {
      if (result) {
        this.setOffer(result)
        this.offerLoaded = true
      }
    })
  }

  /**
   * Changes Offer Funeral Type in the BE
   * @param uuid identification of the offer to be updated
   * @param funeralType the new funeral type to be used
   */
  @action changeOfferFuneralType = (uuid, funeralType) => {
    return backend.changeOfferFuneralType(uuid, funeralType, (result) => {
      this.catalogue.init(result)
      return result.uuid
    })
  }

  /**
   * Applies package to the Offer
   * @param offerUuid identification of the offer to be updated
   * @param packageId identification of the package to be applied
   */
  @action applyPackage = (offerUuid, packageId) => {
    return backend.applyPackage(offerUuid, packageId, (result) => {
      console.log('FINISHED APPLYING PACKAGE')
    })
  }

  /**
   * Orders to save the offer to the backend.
   * If either of the parameters are set / true, the save operation is executed
   * immediately. Otherwise a 2.5sec timeout is set. The timer gets renewed
   * every time another request is made during those 2.5sec.
   * @param sendOfferFinished set to true if the user continues to the checkout
   * @param callback defines what to perform after the offer has been successfully saved
   */
  private saveOffer = (sendOfferFinished: boolean, callback?: () => void) => {
    this.showStatusSaving('saving')
    if (sendOfferFinished || callback) {
      return this.performSaveOffer(sendOfferFinished, callback)
    }
    if (this.saveOfferTimeout) clearTimeout(this.saveOfferTimeout)
    this.saveOfferTimeout = setTimeout(() => {
      this.performSaveOffer(sendOfferFinished, callback)
      this.saveOfferTimeout = null
    }, 2500)
  }

  finishOffer = (callback: () => void) => {
    if (this.isContactDetailsMissing) {
      this.setContactDetailsPopup(true)
    } else {
      this.saveOffer(true, callback)
    }
  }

  performSaveOffer = (sendOfferFinished: boolean, callback?: () => void) => {
    if (this.offer.orderConfirmed) return null

    const productUids = []
    this.offer.products.forEach((product) => {
      productUids.push({
        uuid: product.uuid,
        quantity: product.quantity ? product.quantity : 1
      })
    })
    const interestUids = []
    this.offer.interests.forEach((interest) => {
      interestUids.push({
        uuid: interest.uuid
      })
    })

    const tempOffer = _.clone(this.offer)
    tempOffer.products = productUids
    tempOffer.interests = interestUids

    return backend.saveOffer(tempOffer, sendOfferFinished, (_result) => {
      if (_result) {
        this.showStatusSaving('done')
      } else {
        this.showStatusSaving('error')
      }
      if (_result && callback) callback()
    })
  }

  showStatusSaving = (status: string) => {
    if (status === 'saving') {
      document.getElementById('saveAreaSaving').style.display = 'flex'
    }
    if (status === 'done') {
      document.getElementById('saveAreaSaving').style.display = 'none'
      document.getElementById('saveAreaDone').style.display = 'flex'
      setTimeout(() => {
        document.getElementById('saveAreaDone').style.display = 'none'
      }, 2000)
    }
    if (status === 'error') {
      document.getElementById('saveAreaSaving').style.display = 'none'
      document.getElementById('saveAreaError').style.display = 'flex'
      setTimeout(() => {
        document.getElementById('saveAreaError').style.display = 'none'
      }, 2000)
    }
  }

  cleanProductsByType = (funeralType: FuneralType) => {
    const productsJs = toJS(this.offer.products)
    productsJs.forEach((product, key) => {
      if (!_.some(product.availableFor, funeralType))
        this.offer.products.splice(key, 1)
    })
  }

  cleanProductsByProductType = (productType: ProductType) => {
    toJS(this.offer.products).forEach((product) => {
      if (product.productType === productType) this.removeProduct(product)
    })
  }

  offerHasProduct = (product: Product) => {
    let hasProduct = false
    toJS(this.offer.products).forEach((p) => {
      if (p.uuid === product.uuid) hasProduct = true
    })
    return hasProduct
  }

  getProductInOffer = (uuid: string) => {
    let result = null
    toJS(this.offer.products).forEach((p) => {
      if (p.uuid === uuid) result = p
    })
    return result
  }

  getDiscounts = () => {
    return toJS(this.offer.discounts)
  }

  getProductsOfTypeInOffer = (productType: ProductType) => {
    return toJS(this.offer.products).filter((p) => {
      return p.productType === productType
    })
  }

  offerHasInterest = (interest: Interest) => {
    let hasInterest = false
    toJS(this.offer.interests).forEach((i) => {
      if (i.uuid === interest.uuid) hasInterest = true
    })
    return hasInterest
  }

  // This is necessary as i.e. flowers do only have one price assigned (opposed to cards / obituaries)
  // Therefore, the price is calculated either multiplying it with the quantity or accessing the entries
  // in the prices array
  // TODO this is awkward but it is necessary due product type handling
  calculateProductPrice = (product: Product, quantity: number = 1) => {
    if (['CARD', 'OBITUARY'].includes(product.productType)) {
      return quantity > 10
        ? product.prices.find(
            ({ description }) => description === quantity + ' Stück'
          ).amount / 100
        : toJS(product.prices[0]).amount / 100
    }
    return (product.prices[0].amount * quantity) / 100
  }

  offerProductTotal = () => {
    return toJS(this.offer.products).reduce((sum, product) => {
      return this.calculateProductPrice(product, product.quantity || 1) + sum
    }, 0)
  }

  offerProductTotalWithDiscounts = () => {
    return this.offerProductTotal() + this.getAllDiscounts()
  }

  offerGrandTotal = () => {
    let total =
      this.offerFeesTotal() + this.offerProductTotal() + this.getAllDiscounts()
    if (
      this.offer.planningReason === 'PROVISION' &&
      this.offer.provision.includeTrustAccountCosts
    ) {
      total += 99
    }
    return parseFloat(total.toFixed(2))
  }

  getAllDiscounts = () => {
    if (!this.offer.totalAmountFees) {
      return 0
    }
    let sum = 0
    this.getDiscounts().map((d) => (sum += d.prices[0].amount))
    return sum / 100
  }

  offerFeesTotal = () => {
    if (!this.offer.totalAmountFees) return 0
    return this.offer.totalAmountFees.amount / 100
  }

  getMandateDownloadUrl = () => {
    return this.mandateDownloadUrl
  }

  getProductsInOfferByType = (productType: ProductType) => {
    return this.offer.products.filter((p) => {
      return p.productType === productType
    })
  }

  getExternalFeeSorted = () => {
    const resSorted: ExternalFee[] = _.orderBy(
      this.offer.externalFees,
      ['orderPriority', 'price.amount'],
      ['desc', 'asc']
    )
    console.log('resSorted ->', resSorted)
    return resSorted
  }

  getSortedOfferProducts = () => {
    // TODO evaluate the option to define product types as
    // enums so that interating over the types is possible
    const funerals = this.getProductsInOfferByType('FUNERAL')
    const ceremonies = this.getProductsInOfferByType('CEREMONY')
    const ceremony_others = this.getProductsInOfferByType('CEREMONY_OTHER')
    const coffins = this.getProductsInOfferByType('COFFIN')
    const coffinLinings = this.getProductsInOfferByType('COFFIN_LINING')
    const urns = this.getProductsInOfferByType('URN')
    const flowers = this.getProductsInOfferByType('FLOWER')
    const obituaries = this.getProductsInOfferByType('OBITUARY')
    const cards = this.getProductsInOfferByType('CARD')
    const memorial = this.getProductsInOfferByType('MEMORIAL')
    const others = this.getProductsInOfferByType('OTHERS')
    const tombs = this.getProductsInOfferByType('TOMBS')
    const othersFromZoho = this.getProductsInOfferByType('OTHERS_FROM_ZOHO')
    const othersFromSalesforce = this.getProductsInOfferByType(
      'OTHERS_FROM_SALESFORCE'
    )

    return funerals
      .concat(ceremonies)
      .concat(ceremony_others)
      .concat(coffins)
      .concat(coffinLinings)
      .concat(urns)
      .concat(flowers)
      .concat(obituaries)
      .concat(cards)
      .concat(memorial)
      .concat(others)
      .concat(tombs)
      .concat(othersFromZoho)
      .concat(othersFromSalesforce)
  }
}



// WEBPACK FOOTER //
// ./src/stores/ConfiguratorStore.ts